// frontend/src/utils/tokenUtils.js
import { jwtDecode } from 'jwt-decode';

/**
 * Decode a JWT token and extract its expiration time
 * @param {string} token - The JWT token
 * @returns {Object|null} - Decoded token payload or null if invalid
 */
export const decodeToken = (token) => {
    if (!token) return null;
    
    try {
        return jwtDecode(token);
    } catch (error) {
        console.error('Failed to decode token:', error);
        return null;
    }
};

/**
 * Check if token expires within the specified number of minutes
 * @param {string} token - The JWT token
 * @param {number} minutes - Number of minutes to check (default 60 = 1 hour)
 * @returns {boolean} - True if token expires within the specified time
 */
export const isTokenExpiringSoon = (token, minutes = 60) => {
    const decoded = decodeToken(token);
    if (!decoded || !decoded.exp) return true;
    
    const expirationTime = decoded.exp * 1000; // Convert to milliseconds
    const currentTime = Date.now();
    const timeUntilExpiration = expirationTime - currentTime;
    const minutesUntilExpiration = timeUntilExpiration / (1000 * 60);
    
    return minutesUntilExpiration <= minutes && minutesUntilExpiration > 0;
};

/**
 * Check if token is already expired
 * @param {string} token - The JWT token
 * @returns {boolean} - True if token is expired
 */
export const isTokenExpired = (token) => {
    const decoded = decodeToken(token);
    if (!decoded || !decoded.exp) return true;
    
    const expirationTime = decoded.exp * 1000;
    return Date.now() >= expirationTime;
};

/**
 * Get time until token expires in milliseconds
 * @param {string} token - The JWT token
 * @returns {number} - Milliseconds until expiration (negative if expired)
 */
export const getTimeUntilExpiration = (token) => {
    const decoded = decodeToken(token);
    if (!decoded || !decoded.exp) return 0;
    
    const expirationTime = decoded.exp * 1000;
    return expirationTime - Date.now();
};

/**
 * Calculate when to schedule the next token refresh check
 * Refreshes when token has < 1 hour remaining
 * @param {string} token - The JWT token
 * @returns {number} - Milliseconds until we should check for refresh (or null if token invalid/expired)
 */
export const getNextRefreshCheckTime = (token) => {
    const timeUntilExpiration = getTimeUntilExpiration(token);
    
    if (timeUntilExpiration <= 0) {
        // Token already expired
        return null;
    }
    
    const oneHourInMs = 60 * 60 * 1000;
    
    if (timeUntilExpiration <= oneHourInMs) {
        // Less than 1 hour left - refresh now
        return 0;
    }
    
    // Schedule check for when token has 1 hour remaining
    // Add a small buffer (1 minute) to ensure we don't miss the window
    const bufferMs = 60 * 1000;
    return timeUntilExpiration - oneHourInMs - bufferMs;
};